import styled from 'styled-components';
import {
    Box,
    Button,
    Heading,
    Inline,
    Paragraph,
    Tag,
    createButtonPrimaryColorConfig,
} from '@99designs/emu';
import { colors, spaces } from '@99designs/emu/settings';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { linkTo } from '@99designs/routing';
import { Price } from '@99designs/spa-common';
import { useTrackLink } from '@99designs/tracking';
import type { ComparativeProduct } from '../../types';

export interface ProductCardProps {
    product: ComparativeProduct;
    isContestProduct?: boolean;
}

const colorByCollaborationMethod: Record<string, Record<string, string>> = {
    COLLABORATION_METHOD_CONTEST: {
        backgroundColor: colors.experimental.blue200,
        tagColor: colors.experimental.blue700,
    },
    COLLABORATION_METHOD_ONE_TO_ONE_PROJECT: {
        backgroundColor: colors.experimental.green200,
        tagColor: colors.experimental.green500,
    },
    COLLABORATION_METHOD_STUDIO: {
        backgroundColor: colors.experimental.purple200,
        tagColor: colors.experimental.purple800,
    },
};
const colorByContestTier: Record<string, Record<string, string>> = {
    bronze: {
        backgroundColor: '#FBF0E8',
    },
    silver: {
        backgroundColor: '#F2F1EF',
    },
    gold: {
        backgroundColor: '#FEF0DC',
    },
    platinum: {
        backgroundColor: '#F2F1EF',
    },
};

const Wrapper = styled(Box)<{ collaborationMethod?: string; contestTier?: string }>`
    background-color: ${({ collaborationMethod, contestTier }) =>
        collaborationMethod
            ? colorByCollaborationMethod[collaborationMethod]['backgroundColor']
            : colorByContestTier[contestTier || '']['backgroundColor']};
    border-radius: ${spaces['1bu']};
    margin-top: ${spaces['3bu']};

    > * {
        margin-bottom: ${spaces['1bu']};

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const CollaborationMethodTag = styled(Tag)<{ collaborationMethod: string }>`
    background-color: ${({ collaborationMethod }) =>
        colorByCollaborationMethod[collaborationMethod]['tagColor']};
    border-color: ${({ collaborationMethod }) =>
        colorByCollaborationMethod[collaborationMethod]['tagColor']};
    border-radius: ${spaces['2bu']};

    span {
        color: ${colors.white};
    }
`;

const RecommendationTag = styled(Tag)<{ color: string }>`
    background-color: ${({ color }) => color};
    border-color: ${({ color }) => color};
    border-radius: ${spaces['2bu']};
    margin-bottom: ${spaces['1bu']};
    position: absolute;
    top: 0;
    text-align: center;
    width: 100%;
`;

const PriceWrapper = styled(Inline)`
    align-items: flex-start;

    > * {
        margin-top: 0;
    }
`;

export function ProductCard({ product, isContestProduct = false }: ProductCardProps) {
    const isRecommendedProduct = product.tier && product.tier === 'gold';
    const linkRef = useTrackLink({
        event: 'Clicked CTA on comparative product on product details page',
        meta: { type: 'cta-button', name: product.name },
    });

    const titleByTier: Record<string, Record<string, string>> = {
        bronze: {
            text: __('Bronze'),
        },
        silver: {
            text: __('Silver'),
        },
        gold: {
            text: __('Gold'),
        },
        platinum: {
            text: __('Platinum'),
        },
    };

    const collaborationMethodTag: Record<string, Record<string, string>> = {
        COLLABORATION_METHOD_CONTEST: {
            text: __placeholder('Run a contest'),
        },
        COLLABORATION_METHOD_ONE_TO_ONE_PROJECT: {
            text: __placeholder('1-to-1 Project'),
        },
        COLLABORATION_METHOD_STUDIO: {
            text: __placeholder('Studio'),
        },
    };

    // Temporary fix for wrong category set in the Product Catalog database
    let category = product.category;
    if (
        product.key === 'silver-logo-brand-guide-contest-v2' ||
        product.key === 'gold-logo-brand-guide-contest-v2' ||
        product.key === 'platinum-logo-brand-guide-contest-v2'
    ) {
        category = 'logo-brand-guide';
    }

    return (
        <Box position="relative">
            {isRecommendedProduct && (
                <RecommendationTag
                    appearance="filled"
                    color={isContestProduct ? '#F8DBA9' : colors.experimental.blue300}
                >
                    {__('Recommended')}
                </RecommendationTag>
            )}
            <Wrapper
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                justifyContent="space-between"
                padding="1bu"
                height="100%"
                collaborationMethod={isContestProduct ? undefined : product.collaborationMethod}
                contestTier={isContestProduct ? product.tier : undefined}
            >
                {!isContestProduct && (
                    <CollaborationMethodTag
                        appearance="filled"
                        collaborationMethod={product.collaborationMethod}
                    >
                        {collaborationMethodTag[product.collaborationMethod]['text']}
                    </CollaborationMethodTag>
                )}
                <Box>
                    <Heading appearanceLevel={4} accessibilityLevel={4}>
                        {isContestProduct ? titleByTier[product.tier || '']['text'] : product.title}
                    </Heading>
                    <Paragraph>{product.description}</Paragraph>
                </Box>
                {product.collaborationMethod === 'COLLABORATION_METHOD_STUDIO' ? (
                    <PriceWrapper space="0.5bu">
                        <Heading appearanceLevel={4} accessibilityLevel={4}>
                            {__placeholder('From')}
                        </Heading>
                        <Heading appearanceLevel={3} accessibilityLevel={4}>
                            <Price
                                amount={product.pricing.amountInCents}
                                currency={product.pricing.currency}
                            />
                        </Heading>
                    </PriceWrapper>
                ) : (
                    <Heading appearanceLevel={3} accessibilityLevel={4}>
                        <Price
                            amount={product.pricing.amountInCents}
                            currency={product.pricing.currency}
                        />
                    </Heading>
                )}
                <Button
                    appearance={createButtonPrimaryColorConfig({
                        color: colors.grey800,
                        colorHover: colors.grey900,
                        colorActive: colors.grey900,
                    })}
                    href={
                        isContestProduct
                            ? linkTo.launchBrief(category, {
                                  packageType: product.tier,
                              })
                            : product.url
                    }
                    ref={linkRef}
                >
                    {formatMessage(__('Select {productTitle}'), {
                        productTitle: isContestProduct
                            ? titleByTier[product.tier || '']['text']
                            : product.title.replace(/\bcontest\b/gi, ''),
                    })}
                </Button>
            </Wrapper>
        </Box>
    );
}
